import { useEffect, type ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { Card } from '../../components/ui';
import { LANGS, useLang } from '../../i18n';
import { useAuth } from '../../stores/auth';
import { CONTACT, LEGAL, POLICY_UPDATED, type LegalCopy } from './legal-content';

const mailto = (subject: string) =>
  `mailto:${CONTACT.email}?subject=${encodeURIComponent(subject)}`;

/** Shared frame of /privacy and /contact: readable before signing in, with a language switch. */
function LegalFrame({
  testId,
  children,
}: {
  testId: string;
  children: (c: LegalCopy) => ReactNode;
}) {
  const lang = useLang((s) => s.lang);
  const setLang = useLang((s) => s.setLang);
  const signedIn = !!useAuth((s) => s.token);
  const navigate = useNavigate();
  const { hash } = useLocation();
  const c = LEGAL[lang];

  useEffect(() => {
    if (!hash) return;
    const el = document.getElementById(decodeURIComponent(hash.slice(1)));
    if (el) requestAnimationFrame(() => el.scrollIntoView({ block: 'start' }));
  }, [hash]);

  const back = () =>
    window.history.length > 1 ? navigate(-1) : navigate(signedIn ? '/' : '/login');

  return (
    <div className="absolute inset-0 overflow-y-auto overscroll-y-contain" data-testid={testId}>
      <main className="mx-auto max-w-lg px-4 pt-[calc(var(--safe-top)+16px)] pb-[calc(var(--safe-bottom)+32px)]">
        <div className="flex items-center justify-between">
          <button onClick={back} className="text-sm font-semibold text-ink-300">
            {c.back}
          </button>
          <span className="flex gap-3 text-xs">
            {LANGS.map((l) => (
              <button
                key={l.code}
                onClick={() => setLang(l.code)}
                className={lang === l.code ? 'font-bold text-volt' : 'text-ink-400'}
              >
                {l.label}
              </button>
            ))}
          </span>
        </div>
        {children(c)}
        <LegalLinks className="mt-10" />
      </main>
    </div>
  );
}

function Title({ text, sub }: { text: string; sub?: string }) {
  return (
    <header className="mt-4 flex items-center gap-3">
      <img src="/icons/icon-192.png" alt="" className="size-14 rounded-2xl" />
      <div>
        <h1 className="font-display text-3xl font-bold leading-tight">{text}</h1>
        {sub ? <p className="text-sm text-ink-400">{sub}</p> : null}
      </div>
    </header>
  );
}

/** /privacy — the privacy policy. */
export function PrivacyPage() {
  return (
    <LegalFrame testId="privacy">
      {(c) => (
        <>
          <Title text={c.privacyTitle} sub={`Runner Wars · ${c.updated}: ${POLICY_UPDATED}`} />
          <p className="mt-5 text-sm text-ink-300">{c.intro}</p>
          <div className="mt-2 space-y-3">
            {c.sections.map((s) => (
              <section key={s.id} id={s.id} className="scroll-mt-4 pt-4">
                <h2 className="font-display text-xl font-bold">{s.title}</h2>
                {s.bullets?.length ? (
                  <ul className="mt-2 list-disc space-y-1.5 pl-5 text-sm text-ink-300">
                    {s.bullets.map((b) => (
                      <li key={b}>{b}</li>
                    ))}
                  </ul>
                ) : null}
                {s.text ? <p className="mt-2 text-sm text-ink-300">{s.text}</p> : null}
              </section>
            ))}
          </div>
          <Card className="mt-6 text-sm text-ink-300">
            <a href={`mailto:${CONTACT.email}`} className="font-semibold text-volt">
              {CONTACT.email}
            </a>
            <span className="text-ink-500"> · </span>
            <Link to="/contact" className="font-semibold text-ink-200 underline underline-offset-4">
              {c.contactLink}
            </Link>
          </Card>
        </>
      )}
    </LegalFrame>
  );
}

/** /contact — support, privacy requests and the operator's details. */
export function ContactPage() {
  return (
    <LegalFrame testId="contact">
      {(c) => (
        <>
          <Title text={c.contactTitle} sub="Runner Wars" />
          <p className="mt-5 text-sm text-ink-300">{c.contactIntro}</p>

          <Card className="mt-5 space-y-3">
            <h2 className="font-semibold text-ink-100">{c.supportTitle}</h2>
            <p className="text-sm text-ink-300">{c.supportText}</p>
            <a
              href={mailto(c.supportSubject)}
              className="block rounded-2xl bg-volt py-3 text-center font-bold text-ink-950"
              data-testid="contact-support"
            >
              {c.supportButton}
            </a>
          </Card>

          <div id="xoa-du-lieu" className="scroll-mt-4" />
          <Card className="mt-3 space-y-3">
            <h2 className="font-semibold text-ink-100">{c.deleteTitle}</h2>
            <p className="text-sm text-ink-300">{c.deleteText}</p>
            <a
              href={mailto(c.deleteSubject)}
              className="block rounded-2xl border border-ink-600 py-3 text-center font-bold text-ink-100"
              data-testid="contact-delete"
            >
              {c.deleteButton}
            </a>
            <p className="text-xs text-ink-400">{c.deleteInApp}</p>
          </Card>

          <Card className="mt-3">
            <dl className="space-y-3 text-sm">
              <Row label={c.operatorLabel}>{c.operator}</Row>
              <Row label={c.emailLabel}>
                <a href={`mailto:${CONTACT.email}`} className="font-semibold text-volt">
                  {CONTACT.email}
                </a>
              </Row>
              <Row label={c.phoneLabel}>
                <a href={`tel:${CONTACT.phone}`} className="font-semibold text-ink-100">
                  {CONTACT.phoneDisplay}
                </a>
              </Row>
              <Row label={c.addressLabel}>{c.address}</Row>
            </dl>
          </Card>

          <Card className="mt-3 space-y-2">
            <h2 className="font-semibold text-ink-100">{c.includeTitle}</h2>
            <ul className="list-disc space-y-1 pl-5 text-sm text-ink-300">
              {c.include.map((x) => (
                <li key={x}>{x}</li>
              ))}
            </ul>
            <p className="rounded-xl border border-gold/30 bg-gold/10 px-3 py-2 text-sm text-gold">
              🔒 {c.never}
            </p>
          </Card>

          <div className="mt-4 flex flex-wrap gap-2">
            {c.topics.map((x) => (
              <span
                key={x}
                className="rounded-full border border-ink-700 bg-ink-850 px-3 py-1 text-xs text-ink-300"
              >
                {x}
              </span>
            ))}
          </div>
        </>
      )}
    </LegalFrame>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <dt className="text-xs text-ink-500">{label}</dt>
      <dd className="mt-0.5 text-ink-200">{children}</dd>
    </div>
  );
}

/** "Privacy Policy · Contact" footer links, used on sign-in, Profile, the guide and these pages. */
export function LegalLinks({ className = '' }: { className?: string }) {
  const c = LEGAL[useLang((s) => s.lang)];
  return (
    <p
      className={`flex justify-center gap-3 text-xs text-ink-500 ${className}`}
      data-testid="legal-links"
    >
      <Link to="/privacy" className="underline underline-offset-4">
        {c.privacyLink}
      </Link>
      <span>·</span>
      <Link to="/contact" className="underline underline-offset-4">
        {c.contactLink}
      </Link>
    </p>
  );
}
